import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../../app/appLayout.js';
import { statsApi } from '../../../api/statsApi.js';

const formatYMD = (d) => d.toISOString().slice(0, 10);

const getFirstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const getLastOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const defaultStart = () => formatYMD(getFirstOfMonth(new Date()));
const defaultEnd = () => formatYMD(getLastOfMonth(new Date()));

const formatCurrency = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

/** Parse YYYY-MM-DD as local date to avoid UTC-offset bug (e.g. Jan 1 showing as Dec 31). */
const parseLocalDate = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const StatsScreen = () => {
  const [startDate, setStartDate] = useState(() => defaultStart());
  const [endDate, setEndDate] = useState(() => defaultEnd());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await statsApi.getStats(startDate, endDate);
      if (res.status && res.data) {
        setStats(res.data.results);
      } else {
        setError(res.msg || 'Failed to load stats');
        setStats(null);
      }
    } catch (e) {
      setError(e?.message || 'Error loading stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const categorySpendSorted = useMemo(() => {
    const cat = stats?.categorySpend;
    if (!cat || typeof cat !== 'object') return [];
    return Object.entries(cat)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0));
  }, [stats?.categorySpend]);

  const goToPriorMonth = () => {
    const end = parseLocalDate(endDate);
    const prevEnd = getLastOfMonth(new Date(end.getFullYear(), end.getMonth() - 1, 1));
    const prevStart = getFirstOfMonth(prevEnd);
    setStartDate(formatYMD(prevStart));
    setEndDate(formatYMD(prevEnd));
  };

  const goToNextMonth = () => {
    const end = parseLocalDate(endDate);
    const nextStart = getFirstOfMonth(new Date(end.getFullYear(), end.getMonth() + 1, 1));
    const nextEnd = getLastOfMonth(nextStart);
    setStartDate(formatYMD(nextStart));
    setEndDate(formatYMD(nextEnd));
  };

  const monthLabel = () => {
    const d = parseLocalDate(startDate);
    return d.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', padding: '20px' }}>
        <h1>Stats</h1>

        {/* Date range */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
          <label>
            From
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ marginLeft: '8px' }}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ marginLeft: '8px' }}
            />
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={goToPriorMonth}>
              ← Prior month
            </button>
            <button type="button" onClick={goToNextMonth}>
              Next month →
            </button>
          </div>
          <span style={{ color: '#666' }}>{monthLabel()}</span>
        </div>

        {error && <div style={{ color: 'red' }}>{error}</div>}

        {/* Summary: deposits, withdrawals, net */}
        <section style={{ minWidth: '280px', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '12px' }}>Summary</h2>
          {loading ? (
            <div>Loading…</div>
          ) : stats ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Deposits</span>
                <span>{formatCurrency(stats.deposits)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Withdrawals</span>
                <span>{formatCurrency(stats.withdrawal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '4px' }}>
                <span>Net</span>
                <span style={{ color: (stats.net ?? 0) >= 0 ? 'green' : 'red' }}>
                  {formatCurrency(stats.net)}
                </span>
              </div>
            </div>
          ) : (
            <div>No data for this range.</div>
          )}
        </section>

        {/* Spend by category (from backend categorySpend, sorted most → least) */}
        <section style={{ minWidth: '320px', width: '100%', maxWidth: '500px', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '12px' }}>Spend by category</h2>
          {loading ? (
            <div>Loading…</div>
          ) : categorySpendSorted.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {categorySpendSorted.map((item, i) => (
                <li key={item.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                  <span>{item.name}</span>
                  <span>{formatCurrency(item.amount)}</span>
                </li>
              ))}
            </ul>
          ) : stats ? (
            <p style={{ color: '#666', margin: 0 }}>No category spend in this range.</p>
          ) : null}
        </section>
      </div>
    </AppLayout>
  );
};

export { StatsScreen };
